/**
 * PhoneInput.jsx
 *
 * Reusable phone number input with a country code dropdown.
 * Defaults to +1 (US/Canada). "Other" option reveals a manual
 * code input for international users.
 *
 * Props:
 *   value    — full E.164 string, e.g. "+16165551234" (controlled)
 *   onChange — called with the full E.164 string on every change
 *   id       — HTML id for the local-number input (for <label htmlFor>)
 *   className — optional wrapper class (applied alongside "phone-input-wrap")
 *   disabled — optional
 *
 * The component parses an incoming value into code + local on mount
 * and when the value prop changes externally (e.g. prefill).
 */

import { useEffect, useRef, useState } from 'react';
import './PhoneInput.css';


/** Split a full E.164 string into { code, local }.  */
function parseE164(raw) {
  if (!raw || !raw.startsWith('+')) return { code: '+1', local: raw || '' };

  const digits = raw.slice(1); // everything after the leading +

  // Try +1 first (US/Canada) — most common case
  if (digits.startsWith('1') && digits.length >= 2) {
    return { code: '+1', local: digits.slice(1) };
  }

  // For anything else, try common 2-digit and 3-digit codes
  // (best-effort — we can't perfectly parse every E.164 without a full table,
  //  but "Other" mode lets the user correct it)
  return { code: '', local: raw };
}


export default function PhoneInput({ value, onChange, id, className, disabled }) {
  const [mode, setMode] = useState('us');     // 'us' | 'other'
  const [code, setCode]   = useState('+1');
  const [local, setLocal] = useState('');
  const [otherCode, setOtherCode] = useState('');

  // Track whether we're the ones driving changes (to avoid re-parsing our own output)
  const selfUpdate = useRef(false);

  // Parse incoming value when it changes externally
  useEffect(() => {
    if (selfUpdate.current) {
      selfUpdate.current = false;
      return;
    }

    const parsed = parseE164(value);

    if (parsed.code === '+1') {
      setMode('us');
      setCode('+1');
      setLocal(parsed.local);
      setOtherCode('');
    } else if (parsed.code === '' && value && value.startsWith('+')) {
      // Non-+1 international number — switch to "other" mode
      setMode('other');
      setCode('');
      // Try to split: assume 1-3 digit code after +
      const digits = value.slice(1);
      // Heuristic: try 2-digit, then 3-digit, then 1-digit code
      if (digits.length > 2) {
        setOtherCode('+' + digits.slice(0, 2));
        setLocal(digits.slice(2));
      } else {
        setOtherCode(value);
        setLocal('');
      }
    } else {
      setMode('us');
      setCode('+1');
      setLocal(parsed.local);
    }
  }, [value]);

  // Emit the combined E.164 string
  function emit(nextCode, nextLocal) {
    const cleanLocal = nextLocal.replace(/[\s\-().]/g, '');
    const full = nextCode + cleanLocal;
    selfUpdate.current = true;
    onChange(full);
  }

  function handleModeChange(e) {
    const newMode = e.target.value;
    setMode(newMode);

    if (newMode === 'us') {
      setCode('+1');
      setOtherCode('');
      emit('+1', local);
    } else {
      setCode('');
      emit(otherCode || '+', local);
    }
  }

  function handleOtherCodeChange(e) {
    let val = e.target.value;
    // Ensure it starts with +
    if (val && !val.startsWith('+')) val = '+' + val;
    // Only allow + followed by digits, max 4 digits
    val = val.replace(/[^+\d]/g, '');
    if (val.length > 5) val = val.slice(0, 5); // +XXXX max

    setOtherCode(val);
    emit(val, local);
  }

  function handleLocalChange(e) {
    const val = e.target.value;
    setLocal(val);
    emit(mode === 'us' ? '+1' : otherCode, val);
  }

  return (
    <div className={`phone-input-wrap ${className || ''}`}>
      <div className="phone-input-row">
        <select
          className="phone-input-code-select"
          value={mode}
          onChange={handleModeChange}
          disabled={disabled}
          aria-label="Country code"
        >
          <option value="us">🇺🇸 +1</option>
          <option value="other">Other…</option>
        </select>

        {mode === 'other' && (
          <input
            type="text"
            className="phone-input-other-code"
            value={otherCode}
            onChange={handleOtherCodeChange}
            placeholder="+44"
            disabled={disabled}
            aria-label="Country code"
            size={5}
          />
        )}

        <input
          id={id}
          type="tel"
          className="phone-input-local"
          autoComplete="tel-national"
          placeholder={mode === 'us' ? '6165551234' : 'local number'}
          value={local}
          onChange={handleLocalChange}
          disabled={disabled}
        />
      </div>

      <div className="phone-input-help">
        +1 is the code for the US and Canada.
        not sure about yours?{' '}
        <a
          href="https://countrycode.org"
          target="_blank"
          rel="noopener noreferrer"
          className="phone-input-link"
        >
          look it up →
        </a>
      </div>
    </div>
  );
}
